/* eslint-disable react/react-in-jsx-scope */
import { useContext, useMemo } from "react";
import Brand from "../../../layout/Brand/Brand";
import Navigation, {
  NavigationLine,
} from "../../../layout/Navigation/Navigation";
import { useSelector } from "react-redux";
import User from "../../../layout/User/User";
import {
  masterPageMenu,
  operationPageMenu,
  reportsPageMenu,
  superAdminPageMenu,
  leadsDashboardMenu,
} from "../../../menu";
import ThemeContext from "../../../contexts/themeContext";
import Aside, {
  AsideBody,
  AsideFoot,
  AsideHead,
} from "../../../layout/Aside/Aside";
import { getLoginObjDetails } from "../../../redux/login/login.selector";
import {
  buildGrantSet,
  hasGrantData,
  isAdminLevel,
} from "../../../common/function/menuAccess";

// ---------------------------------------------------------------------------
// Sidebar menu — PURELY grant-driven.
//
// The sidebar shows EXACTLY the pages switched on (Isshow) for this user's role
// in Assign-User-Rights — nothing else. There is NO per-UserLevel hardcoding.
//
// Why the rewrite: the old version hardcoded `hide` per UserLevel and only
// *then* applied grants, which leaked pages two ways — (1) newer master pages
// (Status Master, Sector, Disposition, SIC, Dialler Config, Email Rules, Status
// Fields) were never in the legacy hide-list, so a Level0 user saw them all;
// and (2) the whole Operations group was hidden for Level0 regardless of grants,
// so a granted Pre-Proposals page never appeared. Grants are now the single
// source of truth, matching the URL route guard (common/function/menuAccess).
//
// Rules:
//   - Admins (Level100 / Level4) bypass — see everything.
//   - Level101 keeps its dedicated super-admin menu.
//   - FAIL-SAFE: a role with ZERO grants sees everything, so a tenant whose
//     grants were never seeded is never locked out of the whole app.
//   - A page shows only if `Master|Operations|Reports || <menu text>` is granted.
//     The grant's subMenuName must equal the menu.ts `text` exactly.
// ---------------------------------------------------------------------------

const DefaultAside = () => {
  const { asideStatus, setAsideStatus } = useContext(ThemeContext);
  const loginData = useSelector(getLoginObjDetails);
  const { UserLevel } = loginData;

  const adminLevel = isAdminLevel(loginData);
  const grantSet = useMemo(() => buildGrantSet(loginData), [loginData]);
  // Fail-safe only for a role that was NEVER configured. A role with pages on
  // record but every one switched OFF is enforced — it sees nothing. (Testing
  // `grantSet.size === 0` conflated the two, so switching everything off
  // displayed the entire menu.)
  const noGrants = !hasGrantData(loginData);

  /** Clone a menu group and set each child's `hide` from the grant set.
   *  Returns the clone plus whether ANY child is visible (so an all-hidden
   *  group can be dropped entirely instead of rendering an empty header). */
  const gate = useMemo(
    () => (group: any, rootKey: string, menuName: string) => {
      const clone = JSON.parse(JSON.stringify(group));
      const sub = clone?.[rootKey]?.subMenu;
      if (!sub) return { menu: clone, anyVisible: true };
      let anyVisible = false;
      Object.values(sub).forEach((item: any) => {
        if (!item || !item.text) return;
        const show =
          adminLevel || noGrants
            ? true
            : grantSet.has(`${menuName}||${String(item.text).trim()}`);
        item.hide = !show;
        if (show) anyVisible = true;
      });
      return { menu: clone, anyVisible };
    },
    [adminLevel, noGrants, grantSet],
  );

  const master = useMemo(
    () => gate(masterPageMenu, "masterPages", "Master"),
    [gate],
  );
  const operations = useMemo(
    () => gate(operationPageMenu, "operationPages", "Operations"),
    [gate],
  );
  const reports = useMemo(
    () => gate(reportsPageMenu, "reportsPages", "Reports"),
    [gate],
  );

  // Super admin (Level101) keeps its own dedicated menu, unchanged.
  if (UserLevel === "Level101") {
    return (
      <Aside>
        <AsideHead>
          <Brand asideStatus={asideStatus} setAsideStatus={setAsideStatus} />
        </AsideHead>
        <AsideBody>
          <Navigation menu={superAdminPageMenu} id="aside-master-pages" />
          <NavigationLine />
        </AsideBody>
        <AsideFoot>
          <User />
        </AsideFoot>
      </Aside>
    );
  }

  return (
    <Aside>
      <AsideHead>
        <Brand asideStatus={asideStatus} setAsideStatus={setAsideStatus} />
      </AsideHead>
      <AsideBody>
        {/* Dashboard is ungoverned — always available. */}
        <Navigation menu={leadsDashboardMenu} id="aside-dashboard" />
        {master.anyVisible && (
          <Navigation menu={master.menu} id="aside-master-pages" />
        )}
        {operations.anyVisible && (
          <Navigation menu={operations.menu} id="aside-operation-pages" />
        )}
        {reports.anyVisible && (
          <Navigation menu={reports.menu} id="aside-reports-pages" />
        )}
      </AsideBody>
      <AsideFoot>
        <User />
      </AsideFoot>
    </Aside>
  );
};

export default DefaultAside;
